import PathGame from "./PathGame";
import { Fish } from "lucide-react";

/**
 * Jeff's Weekend — salmon swimming upstream through a deep river.
 * Player: SVG salmon with animated tail and gill flicker.
 * Obstacle: fishing hook + red lure descending from the surface with splash.
 * Lanes: underwater current lines with rising bubbles.
 * Overlay: rippling water surface at top + light rays + kelp silhouettes.
 * Floor: riverbed with pebbles.
 */

function SalmonSprite(size = 34) {
  return (
    <span style={{ display: "inline-block", animation: "salmonSwim 0.9s ease-in-out infinite" }}>
      <svg width={size * 1.4} height={size} viewBox="0 0 56 40" style={{ filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.6))" }} aria-hidden>
        <defs>
          <linearGradient id="salmonBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4A5568" />
            <stop offset="45%" stopColor="#E88B7D" />
            <stop offset="65%" stopColor="#F4A582" />
            <stop offset="100%" stopColor="#F8DFC7" />
          </linearGradient>
        </defs>
        {/* forked caudal tail — narrow at attach point, forked at end */}
        <g style={{ transformOrigin: "14px 20px", animation: "salmonTail 0.35s ease-in-out infinite alternate" }}>
          <path d="M14 20 L4 10 L9 20 L4 30 Z" fill="#B8574A" />
          <path d="M14 20 L6 12 L10 20 L6 28 Z" fill="#D46B5E" opacity="0.7" />
        </g>
        {/* body */}
        <ellipse cx="30" cy="20" rx="20" ry="10" fill="url(#salmonBody)" />
        {/* scale texture */}
        <path d="M20 18 Q22 20 20 22 M24 17 Q26 20 24 23 M28 16 Q30 20 28 24 M32 17 Q34 20 32 23 M36 18 Q38 20 36 22 M40 19 Q41 20 40 21" stroke="#D46B5E" strokeWidth="0.4" fill="none" opacity="0.5" />
        {/* dark spots */}
        <circle cx="28" cy="15" r="1.2" fill="#5C3B2E" opacity="0.6" />
        <circle cx="34" cy="14" r="1" fill="#5C3B2E" opacity="0.6" />
        <circle cx="24" cy="18" r="0.9" fill="#5C3B2E" opacity="0.5" />
        <circle cx="38" cy="18" r="0.8" fill="#5C3B2E" opacity="0.5" />
        {/* lateral pink stripe */}
        <path d="M14 22 Q30 20 48 22" fill="none" stroke="#D4574A" strokeWidth="1.2" opacity="0.6" />
        {/* dorsal fin — animated */}
        <path d="M28 10 L34 6 L38 10 Z" fill="#8B4A3E" style={{ transformOrigin: "33px 10px", animation: "salmonDorsal 1.2s ease-in-out infinite" }} />
        {/* pectoral fin */}
        <path d="M32 22 L28 30 L38 26 Z" fill="#B8574A" opacity="0.85" style={{ transformOrigin: "33px 22px", animation: "salmonPectoral 0.6s ease-in-out infinite alternate" }} />
        {/* gill */}
        <path d="M44 16 Q46 20 44 24" fill="none" stroke="#8B3A2E" strokeWidth="1" />
        {/* head highlight */}
        <ellipse cx="48" cy="18" rx="3" ry="2" fill="#F8DFC7" opacity="0.6" />
        {/* eye */}
        <circle cx="46" cy="18" r="1.8" fill="#F8FAFC" />
        <circle cx="46.3" cy="18" r="1" fill="#0B0D10" />
        <circle cx="46.6" cy="17.5" r="0.4" fill="#FFF" />
        {/* mouth */}
        <path d="M52 21 Q54 22 52 23" fill="none" stroke="#5C2E1E" strokeWidth="0.8" />
        <style>{`
          @keyframes salmonSwim { 0%,100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-2px) rotate(2deg); } }
          @keyframes salmonTail { from { transform: rotate(-14deg); } to { transform: rotate(14deg); } }
          @keyframes salmonDorsal { 0%,100% { transform: skewX(0); } 50% { transform: skewX(-6deg); } }
          @keyframes salmonPectoral { from { transform: rotate(-6deg); } to { transform: rotate(10deg); } }
        `}</style>
      </svg>
    </span>
  );
}

function FishHook() {
  return (
    <svg width="32" height="70" viewBox="0 0 32 70" aria-hidden style={{ filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.5))" }}>
      {/* line */}
      <line x1="16" y1="0" x2="16" y2="34" stroke="#F1F5F9" strokeWidth="1" opacity="0.85" />
      {/* red-white lure body */}
      <ellipse cx="16" cy="40" rx="5" ry="7" fill="#DC2626" />
      <path d="M11 40 L21 40 L21 43 L11 43 Z" fill="#F8FAFC" />
      <circle cx="16" cy="37" r="1" fill="#F8FAFC" />
      <circle cx="16.3" cy="37" r="0.4" fill="#0B0D10" />
      {/* feathers */}
      <path d="M14 47 L12 55 L16 52 Z" fill="#E11D48" opacity="0.7" />
      <path d="M18 47 L20 55 L16 52 Z" fill="#F4A582" opacity="0.7" />
      {/* metallic hook */}
      <path d="M16 47 L16 58 Q16 66 24 64 Q30 62 27 55" fill="none" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M27 55 L30 55 L27 52 Z" fill="#CBD5E1" />
      {/* shine */}
      <line x1="16" y1="50" x2="16" y2="56" stroke="#F8FAFC" strokeWidth="0.6" opacity="0.6" />
    </svg>
  );
}

function WaterLane(index, passed) {
  const bubbleDelay = (index * 0.37) % 3;
  return (
    <>
      {/* horizontal current ripples */}
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 opacity-30" style={{
        height: 1,
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
      }} />
      {/* rising bubbles */}
      <div className="absolute" style={{
        left: `${20 + (index * 13) % 60}%`,
        bottom: 0, width: 3, height: 3,
        borderRadius: "50%",
        background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), rgba(180,220,240,0.3))",
        animation: `bubbleRise 3s ${bubbleDelay}s linear infinite`,
      }} />
      <div className="absolute" style={{
        left: `${40 + (index * 7) % 40}%`,
        bottom: 0, width: 2, height: 2,
        borderRadius: "50%",
        background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8), rgba(180,220,240,0.2))",
        animation: `bubbleRise 4s ${bubbleDelay + 1.5}s linear infinite`,
      }} />
      {/* passed = green shimmer */}
      {passed && (
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse at center, rgba(52,211,153,0.15) 0%, transparent 70%)",
        }} />
      )}
    </>
  );
}

function RiverOverlay() {
  return (
    <>
      {/* water surface at top */}
      <div className="absolute inset-x-0 top-0 pointer-events-none" style={{ height: 18 }}>
        <div className="absolute inset-0" style={{
          background: "linear-gradient(180deg, rgba(180,230,240,0.5) 0%, rgba(100,180,220,0.2) 60%, transparent 100%)",
        }} />
        <svg className="absolute inset-x-0 top-0" width="100%" height="18" viewBox="0 0 400 18" preserveAspectRatio="none" aria-hidden>
          <path d="M0 6 Q40 2 80 6 Q120 10 160 6 Q200 2 240 6 Q280 10 320 6 Q360 2 400 6 L400 0 L0 0 Z" fill="rgba(200,230,240,0.65)">
            <animate attributeName="d" values="M0 6 Q40 2 80 6 Q120 10 160 6 Q200 2 240 6 Q280 10 320 6 Q360 2 400 6 L400 0 L0 0 Z;M0 8 Q40 4 80 8 Q120 12 160 8 Q200 4 240 8 Q280 12 320 8 Q360 4 400 8 L400 0 L0 0 Z;M0 6 Q40 2 80 6 Q120 10 160 6 Q200 2 240 6 Q280 10 320 6 Q360 2 400 6 L400 0 L0 0 Z" dur="4s" repeatCount="indefinite" />
          </path>
        </svg>
      </div>
      {/* god rays */}
      <div className="absolute pointer-events-none" style={{
        top: 12, left: "15%", width: 40, height: "70%",
        background: "linear-gradient(180deg, rgba(180,230,255,0.25), transparent)",
        transform: "skewX(-8deg)", filter: "blur(6px)",
        animation: "rayShimmer 6s ease-in-out infinite",
      }} />
      <div className="absolute pointer-events-none" style={{
        top: 12, right: "22%", width: 30, height: "60%",
        background: "linear-gradient(180deg, rgba(180,230,255,0.18), transparent)",
        transform: "skewX(6deg)", filter: "blur(5px)",
        animation: "rayShimmer 8s ease-in-out infinite reverse",
      }} />
      {/* background small fish schools swimming */}
      <svg className="absolute pointer-events-none" style={{ top: "25%", left: 0, width: "100%", height: 14, animation: "smallFishSchool 18s linear infinite" }} viewBox="0 0 400 14" aria-hidden>
        <g fill="#94A3B8" opacity="0.6">
          <path d="M0 7 L6 4 L6 10 Z M6 7 L10 6 L10 8 Z" />
          <path d="M18 5 L24 2 L24 8 Z M24 5 L28 4 L28 6 Z" />
          <path d="M14 9 L20 6 L20 12 Z M20 9 L24 8 L24 10 Z" />
        </g>
      </svg>
      <svg className="absolute pointer-events-none" style={{ top: "55%", left: 0, width: "100%", height: 12, animation: "smallFishSchool 26s linear infinite reverse" }} viewBox="0 0 400 12" aria-hidden>
        <g fill="#64748B" opacity="0.5" transform="translate(200 0)">
          <path d="M0 6 L5 3 L5 9 Z M5 6 L8 5 L8 7 Z" />
          <path d="M14 4 L19 1 L19 7 Z" />
        </g>
      </svg>
      {/* floating leaf drifting */}
      <div className="absolute pointer-events-none" style={{
        top: "18%", left: 0,
        width: 10, height: 6,
        borderRadius: "60% 40%",
        background: "linear-gradient(45deg, #7A5C2E, #A87A3B)",
        boxShadow: "inset 0 0 2px rgba(0,0,0,0.5)",
        animation: "leafDrift 20s linear infinite",
      }} />
    </>
  );
}

function Riverbed() {
  return (
    <div className="absolute inset-x-0 bottom-0 pointer-events-none" style={{ height: "16%" }}>
      <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, rgba(30,40,55,0.4) 0%, rgba(15,20,30,0.9) 100%)",
      }} />
      {/* swaying kelp */}
      <svg className="absolute inset-x-0 bottom-0" width="100%" height="55" viewBox="0 0 400 55" preserveAspectRatio="none" aria-hidden style={{ overflow: "visible" }}>
        <g stroke="#2D6B4A" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.85">
          <path d="M40 55 Q42 40 38 25 Q36 15 40 5">
            <animate attributeName="d" values="M40 55 Q42 40 38 25 Q36 15 40 5; M40 55 Q38 40 42 25 Q44 15 40 5; M40 55 Q42 40 38 25 Q36 15 40 5" dur="3.5s" repeatCount="indefinite" />
          </path>
          <path d="M120 55 Q118 42 122 28 Q124 18 120 8">
            <animate attributeName="d" values="M120 55 Q118 42 122 28 Q124 18 120 8; M120 55 Q122 42 118 28 Q116 18 120 8; M120 55 Q118 42 122 28 Q124 18 120 8" dur="4.2s" repeatCount="indefinite" />
          </path>
          <path d="M220 55 Q222 40 218 22 Q216 12 220 2">
            <animate attributeName="d" values="M220 55 Q222 40 218 22 Q216 12 220 2; M220 55 Q218 40 222 22 Q224 12 220 2; M220 55 Q222 40 218 22 Q216 12 220 2" dur="3.8s" repeatCount="indefinite" />
          </path>
          <path d="M310 55 Q308 44 312 30 Q314 18 310 10">
            <animate attributeName="d" values="M310 55 Q308 44 312 30 Q314 18 310 10; M310 55 Q312 44 308 30 Q306 18 310 10; M310 55 Q308 44 312 30 Q314 18 310 10" dur="4.6s" repeatCount="indefinite" />
          </path>
          <path d="M375 55 Q377 42 373 26 Q371 16 375 6">
            <animate attributeName="d" values="M375 55 Q377 42 373 26 Q371 16 375 6; M375 55 Q373 42 377 26 Q379 16 375 6; M375 55 Q377 42 373 26 Q371 16 375 6" dur="3.2s" repeatCount="indefinite" />
          </path>
        </g>
      </svg>
      {/* pebbles */}
      <svg className="absolute inset-x-0 bottom-0" width="100%" height="14" viewBox="0 0 400 14" preserveAspectRatio="none" aria-hidden>
        <circle cx="30" cy="10" r="3" fill="#3B4552" />
        <circle cx="55" cy="12" r="2" fill="#4A5568" />
        <circle cx="90" cy="9" r="4" fill="#3B4552" />
        <circle cx="130" cy="11" r="2.5" fill="#4A5568" />
        <circle cx="170" cy="10" r="3" fill="#3B4552" />
        <circle cx="210" cy="12" r="2" fill="#4A5568" />
        <circle cx="245" cy="9" r="3.5" fill="#3B4552" />
        <circle cx="285" cy="11" r="2.5" fill="#4A5568" />
        <circle cx="325" cy="10" r="3" fill="#3B4552" />
        <circle cx="365" cy="12" r="2" fill="#4A5568" />
      </svg>
    </div>
  );
}

export default function Jeff() {
  return (
    <PathGame
      slug="jeff"
      title="Jeff's Weekend"
      headline="Swim · dodge · cash out"
      icon={<Fish className="w-3.5 h-3.5" />}
      actionVerb="Swim"
      laneWord="current"
      playerSprite={SalmonSprite}
      obstacleSprite={FishHook}
      laneVariant={WaterLane}
      backgroundGradient="linear-gradient(180deg,#0F4A6B 0%,#0A3852 35%,#082D42 65%,#051F30 100%)"
      sceneOverlay={<RiverOverlay />}
      sceneFloor={<Riverbed />}
      obstacleKeyframes={`
        @keyframes obstaclePass { 0% { transform: translate(-50%, -140%); } 60% { transform: translate(-50%, 55%); } 70% { transform: translate(-50%, 45%); } 80% { transform: translate(-50%, 55%); } 100% { transform: translate(-50%, 50%); } }
        @keyframes bubbleRise { 0% { transform: translateY(0) translateX(0); opacity: 0; } 15% { opacity: 0.9; } 100% { transform: translateY(-260%) translateX(4px); opacity: 0; } }
        @keyframes rayShimmer { 0%,100% { opacity: 0.8; transform: skewX(-8deg) translateX(0); } 50% { opacity: 1; transform: skewX(-6deg) translateX(3px); } }
        @keyframes smallFishSchool { 0% { transform: translateX(-30%); } 100% { transform: translateX(120%); } }
        @keyframes leafDrift { 0% { transform: translate(-5%, 0) rotate(0deg); } 100% { transform: translate(120%, 30px) rotate(360deg); } }
      `}
      minHeight={260}
    />
  );
}
